import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { useCart } from '../../context/CartContext';
import { Truck, ShieldCheck, CheckCircle2, Lock } from 'lucide-react';

export default function Checkout() {
  const { cart, clearCart } = useCart();
  const navigate = useNavigate();
  const auth = getAuth();

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    pincode: '',
    paymentMethod: 'COD'
  });

  const [loading, setLoading] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderId, setOrderId] = useState('');

  const subtotal = cart.reduce((acc, item) => acc + (item.price * item.quantity), 0);
  const shipping = subtotal > 499 ? 0 : 50;
  const total = subtotal + shipping;
  const totalItemsCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    if (cart.length === 0) return alert("Your cart is empty!");

    const currentUser = auth.currentUser;
    if (!currentUser) {
      alert("Please login first to place an order!");
      navigate('/login'); // Optional: redirect to login if not logged in
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        customerId: currentUser.uid, // Dynamically captured customer ID for Admin Panel mapping
        customerEmail: currentUser.email,
        customerDetails: formData,
        items: cart,
        subtotal,
        shipping,
        total,
        treesPlanted: totalItemsCount,
        status: 'Order Placed',
        createdAt: serverTimestamp()
      };

      const docRef = await addDoc(collection(db, 'orders'), orderData);
      setOrderId(docRef.id);
      setOrderSuccess(true);
      clearCart();
    } catch (error) {
      console.error("Error placing order: ", error);
      alert("Failed to place order. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (orderSuccess) {
    return (
      <div className="min-h-[70vh] bg-gray-100 flex items-center justify-center py-12 px-4">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md w-full border border-gray-200">
          <CheckCircle2 className="h-16 w-16 text-emerald-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Order Placed, Thank You!</h2>
          <p className="text-xs text-gray-500 mb-4">
            Confirmation will be sent to your email. Your Order ID is <span className="font-mono font-bold text-gray-800">{orderId}</span>
          </p>
          <button
            onClick={() => navigate('/my-orders')}
            className="w-full bg-amber-400 hover:bg-amber-500 text-gray-950 py-2.5 rounded-lg font-bold text-xs shadow transition"
          >
            View Your Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        
        {/* Amazon-style Checkout Header */}
        <div className="bg-white px-6 py-4 rounded-lg shadow-sm border border-gray-200 mb-6 flex justify-between items-center">
          <h1 className="text-xl font-bold text-gray-900">Checkout <span className="text-xs font-normal text-gray-500">({totalItemsCount} items)</span></h1>
          <Lock className="h-5 w-5 text-gray-400" />
        </div>

        <form onSubmit={handlePlaceOrder} className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Shipping Form Sections */}
          <div className="lg:col-span-2 space-y-6">
            
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-4">
              <h2 className="text-sm font-bold uppercase text-amber-800 border-b border-gray-200 pb-2">1. Enter a shipping address</h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Full name</label>
                  <input type="text" name="fullName" required value={formData.fullName} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-md focus:ring-1 focus:ring-amber-400 focus:outline-none" placeholder="Lakshay Sharma" />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Email address</label>
                  <input type="email" name="email" required value={formData.email} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-md focus:ring-1 focus:ring-amber-400 focus:outline-none" placeholder="lakshay@gmail.com" />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Phone number</label>
                  <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-md focus:ring-1 focus:ring-amber-400 focus:outline-none" placeholder="9876543210" />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Pincode</label>
                  <input type="text" name="pincode" required value={formData.pincode} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-md focus:ring-1 focus:ring-amber-400 focus:outline-none" placeholder="110098" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-bold text-gray-700 mb-1">Address (House No, Building, Street, Area)</label>
                  <textarea name="address" required rows="2" value={formData.address} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-md focus:ring-1 focus:ring-amber-400 focus:outline-none"></textarea>
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Town/City</label>
                  <input type="text" name="city" required value={formData.city} onChange={handleChange} className="w-full p-2.5 bg-gray-50 border border-gray-300 rounded-md focus:ring-1 focus:ring-amber-400 focus:outline-none" placeholder="New Delhi" />
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 space-y-3">
              <h2 className="text-sm font-bold uppercase text-amber-800 border-b border-gray-200 pb-2">2. Payment method</h2>
              <div className="flex items-center space-x-3 p-3 bg-amber-50/50 border border-amber-200 rounded-md text-xs font-bold text-gray-800">
                <input type="radio" checked readOnly className="text-amber-500 focus:ring-amber-400" />
                <span>Cash on Delivery / Pay on Delivery (Zero-Plastic Package Inspection)</span>
              </div>
            </div>

          </div>

          {/* Right Summary Sidebar & Place Order Button */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 h-fit space-y-4">
            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-amber-400 hover:bg-amber-500 text-gray-950 py-2.5 rounded-lg font-bold text-xs shadow transition disabled:opacity-50"
            >
              {loading ? "Placing Order..." : "Place your order"}
            </button>

            <div className="border-t border-gray-200 pt-4 space-y-2 text-xs">
              <h3 className="font-bold text-gray-900">Order Summary</h3>
              <div className="flex justify-between text-gray-600">
                <span>Items:</span>
                <span>₹{subtotal}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Delivery:</span>
                <span>₹{shipping}</span>
              </div>
              <div className="flex justify-between font-bold text-sm text-rose-800 pt-2 border-t border-gray-100">
                <span>Order Total:</span>
                <span>₹{total}</span>
              </div>
            </div>
          </div>

        </form>

      </div>
    </div>
  );
}